import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Building2, MapPin, Filter, Edit, Trash2, User, Mail, Phone, Briefcase, X, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIVISIONS = ['FBS', 'FCS'];
const CONFERENCES = [
  // FBS Conferences
  'ACC', 'Big Ten', 'Big 12', 'SEC', 'American', 'Conference USA', 'MAC', 'Mountain West', 'Sun Belt', 'Independent',
  // FCS Conferences
  'Big Sky', 'CAA', 'Missouri Valley', 'Southland', 'Big South-OVC', 'Ivy League', 'Patriot League', 'Pioneer League', 'Northeast', 'SWAC', 'MEAC'
];

const emptyContact = { name: '', email: '', phone: '', role: '' };

const AccountsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterConference, setFilterConference] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  
  const [formData, setFormData] = useState({
    university_name: '',
    conference: '',
    division: '',
    state: '',
    contacts: []
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: getAuthHeaders()
      });
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty contacts
      const validContacts = formData.contacts.filter(
        c => c.name || c.email || c.phone || c.role
      );

      const payload = {
        ...formData,
        contacts: validContacts
      };

      if (editAccount) {
        await axios.put(`${API}/accounts/${editAccount.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Account updated successfully');
      } else {
        await axios.post(`${API}/accounts`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Account created successfully');
      }
      fetchAccounts();
      resetForm();
    } catch (error) {
      console.error('Failed to save account:', error);
      toast.error('Failed to save account');
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    try {
      await axios.delete(`${API}/accounts/${accountId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Account deleted successfully');
      fetchAccounts();
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Failed to delete account');
    }
  };

  const resetForm = () => {
    setFormData({
      university_name: '',
      conference: '',
      division: '',
      state: '',
      contacts: []
    });
    setEditAccount(null);
    setIsCreateOpen(false);
  };

  const openEditDialog = (account) => {
    setEditAccount(account);
    setFormData({
      university_name: account.university_name,
      conference: account.conference,
      division: account.division,
      state: account.state,
      contacts: account.contacts || []
    });
    setIsCreateOpen(true);
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { ...emptyContact }]
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setFormData({ ...formData, contacts: newContacts });
  };

  const removeContact = (index) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, i) => i !== index)
    });
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.university_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.contacts?.some(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDivision = filterDivision === 'all' || account.division === filterDivision;
    const matchesConference = filterConference === 'all' || account.conference === filterConference;
    return matchesSearch && matchesDivision && matchesConference;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading accounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            ACCOUNTS
          </h1>
          <p className="text-[#475569] mt-1">Manage NCAA Division I Universities</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateOpen(open); }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold uppercase tracking-wide shadow-lg hover:shadow-xl transition-all"
              data-testid="create-account-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {editAccount ? 'EDIT ACCOUNT' : 'NEW ACCOUNT'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* University Info */}
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">University Name</Label>
                <Input
                  value={formData.university_name}
                  onChange={(e) => setFormData({ ...formData, university_name: e.target.value })}
                  placeholder="e.g., University of Alabama"
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                  data-testid="account-name-input"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Division</Label>
                  <Select
                    value={formData.division}
                    onValueChange={(value) => setFormData({ ...formData, division: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="account-division-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0]">
                      {DIVISIONS.map(div => (
                        <SelectItem key={div} value={div} className="text-[#0F172A] hover:bg-[#F1F5F9]">
                          {div}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Conference</Label>
                  <Select
                    value={formData.conference}
                    onValueChange={(value) => setFormData({ ...formData, conference: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="account-conference-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0]">
                      {CONFERENCES.map(conf => (
                        <SelectItem key={conf} value={conf} className="text-[#0F172A] hover:bg-[#F1F5F9]">
                          {conf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="e.g., Alabama"
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                  data-testid="account-state-input"
                  required
                />
              </div>

              {/* Contacts Section */}
              <div className="pt-4 border-t border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">
                    Contacts
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addContact}
                    className="border-[#E2E8F0] text-[#007AFF] hover:bg-[#EFF6FF]"
                    data-testid="add-contact-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Contact
                  </Button>
                </div>
                
                {formData.contacts.length === 0 ? (
                  <div className="text-center py-6 bg-[#F8FAFC] rounded-lg border border-dashed border-[#E2E8F0]">
                    <Users className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-[#94A3B8] text-sm">No contacts added yet</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addContact}
                      className="mt-2 text-[#007AFF] hover:text-[#0066CC]"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add your first contact
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.contacts.map((contact, index) => (
                      <div key={index} className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] relative">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeContact(index)}
                          className="absolute top-2 right-2 h-6 w-6 text-[#94A3B8] hover:text-[#FF3B30] hover:bg-[#FEF2F2]"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        
                        <div className="text-xs text-[#94A3B8] uppercase tracking-wider mb-3 font-medium">
                          Contact {index + 1}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              value={contact.name}
                              onChange={(e) => updateContact(index, 'name', e.target.value)}
                              placeholder="Name"
                              className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A] h-9"
                              data-testid={`contact-name-${index}`}
                            />
                          </div>
                          
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              value={contact.role}
                              onChange={(e) => updateContact(index, 'role', e.target.value)}
                              placeholder="Role"
                              className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A] h-9"
                              data-testid={`contact-role-${index}`}
                            />
                          </div>
                          
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              type="email"
                              value={contact.email}
                              onChange={(e) => updateContact(index, 'email', e.target.value)}
                              placeholder="Email"
                              className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A] h-9"
                              data-testid={`contact-email-${index}`}
                            />
                          </div>
                          
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              type="tel"
                              value={contact.phone}
                              onChange={(e) => updateContact(index, 'phone', e.target.value)}
                              placeholder="Phone"
                              className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A] h-9"
                              data-testid={`contact-phone-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1 border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold"
                  data-testid="account-submit-btn"
                >
                  {editAccount ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search universities or contacts..."
                className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                data-testid="search-accounts-input"
              />
            </div>
            
            <Select value={filterDivision} onValueChange={setFilterDivision}>
              <SelectTrigger className="w-full md:w-40 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-division-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Division" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Divisions</SelectItem>
                {DIVISIONS.map(div => (
                  <SelectItem key={div} value={div} className="text-[#0F172A]">{div}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterConference} onValueChange={setFilterConference}>
              <SelectTrigger className="w-full md:w-48 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-conference-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Conference" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Conferences</SelectItem>
                {CONFERENCES.map(conf => (
                  <SelectItem key={conf} value={conf} className="text-[#0F172A]">{conf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0] hover:bg-transparent bg-[#F8FAFC]">
                <TableHead className="text-[#475569] font-semibold">University</TableHead>
                <TableHead className="text-[#475569] font-semibold">Contacts</TableHead>
                <TableHead className="text-[#475569] font-semibold">Conference</TableHead>
                <TableHead className="text-[#475569] font-semibold">Division</TableHead>
                <TableHead className="text-[#475569] font-semibold">State</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-[#94A3B8]">
                    No accounts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccounts.map((account) => (
                  <TableRow 
                    key={account.id} 
                    className="border-[#E2E8F0] hover:bg-[#F8FAFC]"
                    data-testid={`account-row-${account.id}`}
                  >
                    <TableCell className="font-medium text-[#0F172A]">
                      <div 
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => openEditDialog(account)}
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center group-hover:bg-[#007AFF]/20 transition-colors">
                          <Building2 className="w-5 h-5 text-[#007AFF]" />
                        </div>
                        <p className="font-medium group-hover:text-[#007AFF] transition-colors">{account.university_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {account.contacts?.length > 0 ? (
                        <div className="space-y-2">
                          {account.contacts.slice(0, 2).map((contact, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-[#0F172A]">{contact.name || 'Unnamed'}</span>
                                {contact.role && (
                                  <Badge className="bg-[#F1F5F9] text-[#475569] text-xs border-0">
                                    {contact.role}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-[#94A3B8] mt-0.5">
                                {contact.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {contact.email}
                                  </span>
                                )}
                                {contact.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {contact.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {account.contacts.length > 2 && (
                            <Badge className="bg-[#007AFF]/10 text-[#007AFF] text-xs border-0">
                              +{account.contacts.length - 2} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#94A3B8] text-sm">No contacts</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0">
                        {account.conference}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={account.division === 'FBS' 
                          ? 'bg-[#10B981]/10 text-[#059669] border-0' 
                          : 'bg-[#F59E0B]/10 text-[#D97706] border-0'
                        }
                      >
                        {account.division}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#475569]">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {account.state}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(account)}
                          className="text-[#475569] hover:text-[#007AFF] hover:bg-[#EFF6FF]"
                          data-testid={`edit-account-${account.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(account.id)}
                          className="text-[#475569] hover:text-[#FF3B30] hover:bg-[#FEF2F2]"
                          data-testid={`delete-account-${account.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-[#94A3B8]">
        <span>Showing {filteredAccounts.length} of {accounts.length} accounts</span>
        <div className="flex gap-4">
          <span>FBS: {accounts.filter(a => a.division === 'FBS').length}</span>
          <span>FCS: {accounts.filter(a => a.division === 'FCS').length}</span>
        </div>
      </div>
    </div>
  );
};

export default AccountsPage;
